export type ChatAgentError = {
    readonly code: string;
    readonly message: string;
};

/**
 * How an own run ended. `interrupt` is a run that finished waiting on the user's input, `disconnected`
 * a stream that closed without a terminal event, `rejected` a request the server turned away before
 * the run started.
 */
export type ChatRunOutcome = 'aborted' | 'disconnected' | 'error' | 'interrupt' | 'rejected' | 'success';

/**
 * Why the server turned a request away before its run started; the user's input is kept. A rejected
 * answer leaves its question as the server holds it: answerable again, decided, or expired.
 */
export type ChatNotice = 'answer-failed' | 'busy' | 'question-expired' | 'send-failed';

/**
 * The thread's own run: none yet, one in flight, or the latest one's result. Its anchor is the user
 * message that opened the turn the run belongs to.
 */
export type ChatRunPhase =
    | { readonly phase: 'idle' }
    | { readonly anchorMessageId: string | null; readonly phase: 'running' }
    | {
          readonly anchorMessageId: string | null;
          readonly error: ChatAgentError | null;
          readonly outcome: ChatRunOutcome;
          readonly phase: 'settled';
          readonly rejection: ChatNotice | null;
      };

/** `loading` is only the first load; a later sync keeps what is shown until its snapshot arrives. */
export type ChatHistoryStatus = 'failed' | 'loaded' | 'loading' | 'unloaded';

export const IDLE_RUN: ChatRunPhase = { phase: 'idle' };

/**
 * Whether the run ended in an error the user saw on its turn. A stop counts, as it ends the turn for
 * the user; a rejected continue or answer does not, as it leaves the turn as it was.
 */
export const isVisibleRunError = (run: ChatRunPhase): boolean => {
    if (run.phase !== 'settled') return false;
    switch (run.outcome) {
        case 'aborted':
        case 'disconnected':
        case 'error':
            return true;
        case 'rejected':
            return run.rejection === 'busy' || run.rejection === 'send-failed';
        case 'interrupt':
        case 'success':
            return false;
    }
};

/** What the own run shows on its turn: still working, or failed where the user can see it. */
export type ChatLiveTurn = {
    readonly anchorMessageId: string | null;
    readonly status: 'error' | 'pending';
};

export const liveTurnOf = (run: ChatRunPhase): ChatLiveTurn | null => {
    if (run.phase === 'idle') return null;
    if (run.phase === 'running') return { anchorMessageId: run.anchorMessageId, status: 'pending' };
    return isVisibleRunError(run) && run.outcome !== 'aborted'
        ? { anchorMessageId: run.anchorMessageId, status: 'error' }
        : null;
};
